import { SidenavItem } from "@/constants/sidenav.config";

export interface NavEnrichResult {
	count?: number;
	bubbleColor?: "red" | "yellow" | "green" | "orange";
	bubbleText?: React.ReactNode;
}

/**
 * Segment-boundary route match shared by SidebarItem and SidebarGroup:
 * "/app/help" is active on "/app/help" and "/app/help/x", but NOT on
 * "/app/help-admin" (a plain substring `includes` lit up both Help Center and
 * Help Articles). `strictPath` forces an exact match — needed when one nav
 * path is a prefix of a sibling's (e.g. "/app/contacts" vs "/app/contacts/companies").
 */
export const isNavPathActive = (
	pathname: string,
	item: Pick<SidenavItem, "path" | "strictPath">
): boolean => {
	if (!item.path) {
		return false;
	}

	if (item.strictPath) {
		return pathname === item.path;
	}

	return pathname === item.path || pathname.startsWith(`${item.path}/`);
};

/**
 * Applies the permission/feature/company gate to a nav item list, descending
 * into expandable groups: the group itself is gated, its children are gated
 * individually, and a group whose children are all filtered out disappears.
 */
export const filterNavItems = (
	items: SidenavItem[],
	passes: (item: SidenavItem) => boolean
): SidenavItem[] =>
	items.reduce<SidenavItem[]>((visible, item) => {
		if (!passes(item)) {
			return visible;
		}

		if (!item.subItems) {
			visible.push(item);

			return visible;
		}

		const subItems = item.subItems.filter(passes);

		if (subItems.length > 0) {
			visible.push({ ...item, subItems });
		}

		return visible;
	}, []);
